/**
 * Monitoring Service for AfterService digital assistant
 * Provides comprehensive observability, performance tracking, and analytics
 * Maintains user privacy while gathering valuable system insights
 */

import { getMonitoringConfig, getPerformanceConfig, getApiConfig } from '../config/config';

// Monitoring event types
export const MONITORING_EVENTS = {
  PAGE_VIEW: 'page_view',
  USER_ACTION: 'user_action',
  API_CALL: 'api_call',
  ERROR: 'error',
  PERFORMANCE: 'performance',
  FEATURE_USAGE: 'feature_usage',
  SESSION_START: 'session_start',
  SESSION_END: 'session_end'
};

// Performance metrics
export const PERFORMANCE_METRICS = {
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',
  CHAT_MESSAGE_TIME: 'chat_message_time',
  SEARCH_TIME: 'search_time',
  RENDER_TIME: 'render_time',
  BUNDLE_SIZE: 'bundle_size'
};

// ---------------------------------------------------------------------------
// P3-11: privacy-preserving claims funnel instrumentation
//
// Design contract (APP 3 data minimisation - see audit/claims-uplift-scoping):
//   - CLOSED enum of event names. Anything else is dropped client-side.
//   - meta is schema-allowlisted: only {stage, tier, source} survive, and each
//     value must itself pass a closed validation (integer / known tier string /
//     'curated'|'ai'). Free text is STRUCTURALLY impossible - there is no
//     string field that accepts arbitrary values, so a condition name or any
//     health vocabulary cannot transit this pipeline.
//   - Anonymous rotating session UUID kept in sessionStorage ONLY (never
//     localStorage, never a userId, never a Cognito sub). It dies with the tab.
//   - Batched POST to `${analyticsEndpoint}/analytics/events` using plain
//     fetch with credentials:'omit' and NO Authorization header. We do NOT use
//     apiService's authed axios client: (a) apiService imports this module, so
//     that would be a circular dependency, and (b) the authed client attaches
//     the Cognito JWT, which would carry identity to an anonymous pipeline.
//   - If config.analyticsEndpoint is empty the whole feature is a safe no-op:
//     events are dropped in the browser and nothing ever hits the network.
// ---------------------------------------------------------------------------

// Closed claims-funnel step enum. Server (analytics_events_handler_v1.py)
// validates against the same list - keep the two in lockstep.
export const CLAIMS_FUNNEL_STEPS = Object.freeze({
  CONDITION_ADDED: 'condition_added',
  DIAGNOSIS_RECORDED: 'diagnosis_recorded',
  NARRATIVE_DECLARED: 'narrative_declared',
  EVIDENCE_DECLARED: 'evidence_declared',
  CHECKLIST_GENERATED_CURATED: 'checklist_generated_curated',
  CHECKLIST_GENERATED_AI: 'checklist_generated_ai',
  CHECK_ANSWERS_VIEWED: 'check_answers_viewed',
  DECLARATION_COMPLETED: 'declaration_completed',
  LODGE_PANEL_VIEWED: 'lodge_panel_viewed',
  MYSERVICE_CLICKED: 'myservice_clicked',
  PACK_DOWNLOADED: 'pack_downloaded',
  MARKED_LODGED: 'marked_lodged',
  STAGE_ADVANCED: 'stage_advanced',
  LETTER_INTERPRETED: 'letter_interpreted',
  DECISION_INTERPRETED: 'decision_interpreted',
  REVIEW_LANE_CHOSEN: 'review_lane_chosen',
  DIGEST_OPTOUT: 'digest_optout'
});

const FUNNEL_STEP_SET = new Set(Object.values(CLAIMS_FUNNEL_STEPS));

// Known readiness tiers - mirrors TIER_ORDER in utils/claimsScoring.js.
// Deliberately a copy, not an import: the analytics module must stay a leaf
// with no claims-domain dependencies. A contract test asserts the two lists
// never drift (monitoringService.funnel.test.js).
export const CLAIMS_FUNNEL_META_TIERS = Object.freeze([
  'Just started',
  'In progress',
  'Nearly there',
  'Ready to lodge'
]);

export const CLAIMS_FUNNEL_META_SOURCES = Object.freeze(['curated', 'ai']);

const FUNNEL_META_MAX_STAGE = 20;
const FUNNEL_BATCH_SIZE = 10;
const FUNNEL_FLUSH_INTERVAL_MS = 30000;
const FUNNEL_SESSION_STORAGE_KEY = 'as_funnel_sid';
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when `step` is a member of the closed CLAIMS_FUNNEL_STEPS enum.
 * @param {unknown} step
 * @returns {boolean}
 */
export function isClaimsFunnelStep(step) {
  return typeof step === 'string' && FUNNEL_STEP_SET.has(step);
}

/**
 * Reduce arbitrary caller-supplied meta to the closed allowlist.
 * Only three keys can survive, and each value must pass its own closed
 * validation - everything else (unknown keys, wrong types, free text,
 * condition names, nested objects) is stripped, never rejected loudly.
 *
 * @param {unknown} meta
 * @returns {{stage?: number, tier?: string, source?: string}}
 */
export function sanitizeFunnelMeta(meta) {
  const out = {};
  if (meta === null || typeof meta !== 'object' || Array.isArray(meta)) {
    return out;
  }
  const has = (key) => Object.prototype.hasOwnProperty.call(meta, key);
  if (has('stage') && Number.isInteger(meta.stage)
      && meta.stage >= 0 && meta.stage <= FUNNEL_META_MAX_STAGE) {
    out.stage = meta.stage;
  }
  if (has('tier') && typeof meta.tier === 'string'
      && CLAIMS_FUNNEL_META_TIERS.includes(meta.tier)) {
    out.tier = meta.tier;
  }
  if (has('source') && CLAIMS_FUNNEL_META_SOURCES.includes(meta.source)) {
    out.source = meta.source;
  }
  return out;
}

/**
 * Anonymous UUID v4 - crypto.randomUUID when available, getRandomValues
 * fallback for older environments. Never derived from user identity.
 * @returns {string}
 */
function generateAnonymousUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-resort non-crypto fallback (jsdom edge cases) - still anonymous.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

class MonitoringService {
  constructor() {
    this.monitoringConfig = getMonitoringConfig();
    this.performanceConfig = getPerformanceConfig();
    this.eventQueue = [];
    this.performanceQueue = [];
    this.sessionData = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      pageViews: 0,
      userActions: 0,
      apiCalls: 0,
      errors: 0
    };
    
    this.isEnabled = this.monitoringConfig.enableRequestTracking;
    this.batchSize = 10;
    this.flushInterval = 30000; // 30 seconds

    // P3-11 funnel state - independent of enableRequestTracking: the funnel
    // is gated solely on analyticsEndpoint being configured.
    this.funnelQueue = [];
    this._funnelDropNoticed = false;
    this._funnelLifecycleAttached = false;
    this._funnelFlushTimer = null;
    this._funnelMemSid = null;

    if (this.isEnabled) {
      this.initializeMonitoring();
    }
  }

  /**
   * Initialize monitoring systems
   */
  initializeMonitoring() {
    // Start performance monitoring
    this.initializePerformanceMonitoring();
    
    // Start batch processing
    this.startBatchProcessing();
    
    // Monitor page visibility for session tracking
    this.initializeSessionTracking();
    
    // Track initial page load
    this.trackPageView('/', { initial: true });
  }

  /**
   * Initialize performance monitoring
   */
  initializePerformanceMonitoring() {
    // Track page load performance
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const timing = window.performance.timing;
          const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
          
          this.trackPerformance(PERFORMANCE_METRICS.PAGE_LOAD_TIME, pageLoadTime, {
            domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
            firstPaint: timing.responseEnd - timing.navigationStart,
            networkTime: timing.responseEnd - timing.fetchStart
          });
        }, 100);
      });
    }

    // Track Core Web Vitals if available
    if ('web-vital' in window) {
      this.trackCoreWebVitals();
    }

    // Monitor memory usage
    if (performance.memory) {
      setInterval(() => {
        this.trackPerformance('memory_usage', performance.memory.usedJSHeapSize, {
          totalSize: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit
        });
      }, 60000); // Every minute
    }
  }

  /**
   * Initialize session tracking
   */
  initializeSessionTracking() {
    // Track session start
    this.trackEvent(MONITORING_EVENTS.SESSION_START, {
      sessionId: this.sessionData.sessionId,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer
    });

    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });

    // Track session activity
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('session_pause', { sessionId: this.sessionData.sessionId });
      } else {
        this.trackEvent('session_resume', { sessionId: this.sessionData.sessionId });
      }
    });
  }

  /**
   * Start batch processing for events
   */
  startBatchProcessing() {
    setInterval(() => {
      this.flushEventQueue();
      this.flushPerformanceQueue();
    }, this.flushInterval);
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track page view
   */
  trackPageView(path, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.pageViews++;
    
    this.trackEvent(MONITORING_EVENTS.PAGE_VIEW, {
      path,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      pageViews: this.sessionData.pageViews,
      ...metadata
    });
  }

  /**
   * Track user action
   */
  trackUserAction(action, category, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.userActions++;
    
    this.trackEvent(MONITORING_EVENTS.USER_ACTION, {
      action,
      category,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });
  }

  /**
   * Track API call
   */
  trackApiCall(endpoint, method, duration, status, metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.apiCalls++;
    
    this.trackEvent(MONITORING_EVENTS.API_CALL, {
      endpoint,
      method,
      duration,
      status,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });

    // Also track as performance metric
    this.trackPerformance(PERFORMANCE_METRICS.API_RESPONSE_TIME, duration, {
      endpoint,
      method,
      status
    });
  }

  /**
   * Track error
   */
  trackError(error, context, severity = 'medium', metadata = {}) {
    if (!this.isEnabled) return;

    this.sessionData.errors++;
    
    this.trackEvent(MONITORING_EVENTS.ERROR, {
      error: error.message || error.toString(),
      context,
      severity,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      stack: error.stack ? error.stack.substring(0, 500) : null, // Limit stack trace
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...metadata
    });
  }

  /**
   * Track performance metric
   */
  trackPerformance(metric, value, metadata = {}) {
    if (!this.isEnabled || !this.performanceConfig.preloadRoutes) return;

    this.performanceQueue.push({
      metric,
      value,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      url: window.location.href,
      ...metadata
    });

    // Auto-flush if queue is getting large
    if (this.performanceQueue.length >= this.batchSize * 2) {
      this.flushPerformanceQueue();
    }
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature, action, metadata = {}) {
    if (!this.isEnabled) return;

    this.trackEvent(MONITORING_EVENTS.FEATURE_USAGE, {
      feature,
      action,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      ...metadata
    });
  }

  /**
   * Track chat message performance
   */
  trackChatMessage(messageLength, responseTime, success, metadata = {}) {
    this.trackPerformance(PERFORMANCE_METRICS.CHAT_MESSAGE_TIME, responseTime, {
      messageLength,
      success,
      ...metadata
    });

    this.trackFeatureUsage('chat', success ? 'message_sent' : 'message_failed', {
      messageLength,
      responseTime,
      ...metadata
    });
  }

  /**
   * Track search performance
   */
  trackSearch(query, resultCount, searchTime, metadata = {}) {
    this.trackPerformance(PERFORMANCE_METRICS.SEARCH_TIME, searchTime, {
      queryLength: query.length,
      resultCount,
      ...metadata
    });

    this.trackFeatureUsage('search', 'executed', {
      queryLength: query.length,
      resultCount,
      searchTime,
      ...metadata
    });
  }

  /**
   * Generic event tracking
   */
  trackEvent(eventType, data) {
    if (!this.isEnabled) return;

    const event = {
      type: eventType,
      timestamp: Date.now(),
      sessionId: this.sessionData.sessionId,
      url: window.location.href,
      ...data
    };

    this.eventQueue.push(event);

    // Auto-flush if queue is getting large
    if (this.eventQueue.length >= this.batchSize * 2) {
      this.flushEventQueue();
    }
  }

  /**
   * Track session end
   */
  trackSessionEnd() {
    const sessionDuration = Date.now() - this.sessionData.startTime;
    
    this.trackEvent(MONITORING_EVENTS.SESSION_END, {
      sessionId: this.sessionData.sessionId,
      duration: sessionDuration,
      pageViews: this.sessionData.pageViews,
      userActions: this.sessionData.userActions,
      apiCalls: this.sessionData.apiCalls,
      errors: this.sessionData.errors
    });

    // Force flush remaining events
    this.flushEventQueue();
    this.flushPerformanceQueue();
  }

  /**
   * Flush event queue
   */
  flushEventQueue() {
    if (this.eventQueue.length === 0) return;

    const events = this.eventQueue.splice(0, this.batchSize);
    
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 Monitoring Events');
      events.forEach(event => {
        console.log(`${event.type}:`, event);
      });
      console.groupEnd();
    }

    // TODO: Send to monitoring service (CloudWatch, DataDog, etc.)
    // this.sendToMonitoringService(events);
  }

  /**
   * Flush performance queue
   */
  flushPerformanceQueue() {
    if (this.performanceQueue.length === 0) return;

    const metrics = this.performanceQueue.splice(0, this.batchSize);
    
    if (process.env.NODE_ENV === 'development') {
      console.group('⚡ Performance Metrics');
      metrics.forEach(metric => {
        console.log(`${metric.metric}: ${metric.value}ms`, metric);
      });
      console.groupEnd();
    }

    // TODO: Send to performance monitoring service
    // this.sendToPerformanceService(metrics);
  }

  // -------------------------------------------------------------------------
  // P3-11: claims funnel instrumentation
  // -------------------------------------------------------------------------

  /**
   * Resolve the analytics endpoint at call time (empty string = feature off).
   * @returns {string}
   */
  _analyticsEndpoint() {
    try {
      return (getApiConfig().analyticsEndpoint || '').trim();
    } catch (e) {
      return '';
    }
  }

  /**
   * Anonymous rotating session id - sessionStorage only (dies with the tab),
   * never localStorage, never a userId. Falls back to an in-memory id when
   * storage is unavailable (e.g. private browsing restrictions).
   * @returns {string}
   */
  _funnelSessionId() {
    try {
      const existing = window.sessionStorage.getItem(FUNNEL_SESSION_STORAGE_KEY);
      if (existing && UUID_V4_RE.test(existing)) {
        return existing;
      }
      const sid = generateAnonymousUuid();
      window.sessionStorage.setItem(FUNNEL_SESSION_STORAGE_KEY, sid);
      return sid;
    } catch (e) {
      if (!this._funnelMemSid) {
        this._funnelMemSid = generateAnonymousUuid();
      }
      return this._funnelMemSid;
    }
  }

  /**
   * Track a claims-funnel step.
   *
   * @param {string} step - MUST be a value from CLAIMS_FUNNEL_STEPS; anything
   *   else is dropped client-side (the enum is the contract).
   * @param {object} [meta] - Schema-allowlisted; only
   *   {stage: integer, tier: known-tier-string, source: 'curated'|'ai'}
   *   survive sanitisation. All other keys/values are stripped.
   */
  trackFunnel(step, meta = {}) {
    if (!isClaimsFunnelStep(step)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`trackFunnel: dropped unknown step "${String(step)}"`);
      }
      return;
    }

    if (!this._analyticsEndpoint()) {
      // Feature not configured - drop silently. One debug line, dev only.
      if (process.env.NODE_ENV === 'development' && !this._funnelDropNoticed) {
        this._funnelDropNoticed = true;
        console.debug('trackFunnel: analyticsEndpoint not configured - funnel events are dropped client-side.');
      }
      return;
    }

    this.funnelQueue.push({
      event: step,
      ts: Date.now(),
      sessionId: this._funnelSessionId(),
      meta: sanitizeFunnelMeta(meta)
    });

    this._ensureFunnelLifecycle();

    if (this.funnelQueue.length >= FUNNEL_BATCH_SIZE) {
      this.flushFunnelQueue();
    }
  }

  /**
   * POST queued funnel events as one batch. Fire-and-forget: failures are
   * swallowed and the batch is dropped - analytics must never affect the app,
   * and retry storms are worse than a lost breadcrumb.
   */
  flushFunnelQueue() {
    if (this.funnelQueue.length === 0) return;

    const endpoint = this._analyticsEndpoint();
    if (!endpoint) {
      this.funnelQueue = [];
      return;
    }

    const events = this.funnelQueue.splice(0, this.funnelQueue.length);
    const url = `${endpoint.replace(/\/+$/, '')}/analytics/events`;

    try {
      // Plain fetch, credentials omitted, no Authorization header:
      // the pipeline is anonymous by construction.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
        credentials: 'omit'
      }).catch(() => { /* drop - best-effort analytics */ });
    } catch (e) {
      // fetch unavailable or threw synchronously - drop the batch.
    }
  }

  /**
   * Lazily attach flush triggers (interval + page-hide) the first time a
   * funnel event is queued, so an unconfigured app never pays the cost.
   */
  _ensureFunnelLifecycle() {
    if (this._funnelLifecycleAttached) return;
    this._funnelLifecycleAttached = true;

    this._funnelFlushTimer = setInterval(() => this.flushFunnelQueue(), FUNNEL_FLUSH_INTERVAL_MS);

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushFunnelQueue());
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.flushFunnelQueue();
      });
    }
  }

  /**
   * Test/teardown helper - stop the lazy funnel flush interval.
   */
  stopFunnelFlushTimer() {
    if (this._funnelFlushTimer) {
      clearInterval(this._funnelFlushTimer);
      this._funnelFlushTimer = null;
      this._funnelLifecycleAttached = false;
    }
  }

  /**
   * Get current session data
   */
  getSessionData() {
    return {
      ...this.sessionData,
      currentDuration: Date.now() - this.sessionData.startTime
    };
  }

  /**
   * Get monitoring statistics
   */
  getStatistics() {
    return {
      session: this.getSessionData(),
      queueSizes: {
        events: this.eventQueue.length,
        performance: this.performanceQueue.length
      },
      isEnabled: this.isEnabled
    };
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`📊 Monitoring ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const monitoringService = new MonitoringService();

// Export convenience methods
export const trackPageView = (path, metadata) => monitoringService.trackPageView(path, metadata);
export const trackUserAction = (action, category, metadata) => monitoringService.trackUserAction(action, category, metadata);
export const trackApiCall = (endpoint, method, duration, status, metadata) => monitoringService.trackApiCall(endpoint, method, duration, status, metadata);
export const trackError = (error, context, severity, metadata) => monitoringService.trackError(error, context, severity, metadata);
export const trackPerformance = (metric, value, metadata) => monitoringService.trackPerformance(metric, value, metadata);
export const trackFeatureUsage = (feature, action, metadata) => monitoringService.trackFeatureUsage(feature, action, metadata);
export const trackChatMessage = (messageLength, responseTime, success, metadata) => monitoringService.trackChatMessage(messageLength, responseTime, success, metadata);
export const trackSearch = (query, resultCount, searchTime, metadata) => monitoringService.trackSearch(query, resultCount, searchTime, metadata);
// P3-11: claims funnel - closed-enum steps + allowlisted meta only.
export const trackFunnel = (step, meta) => monitoringService.trackFunnel(step, meta);
export const flushFunnelQueue = () => monitoringService.flushFunnelQueue();

// Export monitoring service instance
export default monitoringService; 